import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import '../../college-theme.css';
import { DeskButton, DeskLink, Eyebrow } from './essayShared';

// Where an invite link lands. The student's Access screen mints the link; this
// is the "they confirm who they are before it works" half of that promise —
// the signed-in address has to be the one that was invited.
export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/college-access/invites/${token}`)
      .then((res) => {
        if (alive) setInvite(res.data.invite);
      })
      .catch((err) => {
        if (alive) setError(err.response?.data?.error || 'That invite could not be opened');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api, token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await api.post(`/college-access/invites/${token}/accept`);
      toast.success('You are in');
      navigate(`/shared/${res.data.grant.id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to accept that invite');
      setAccepting(false);
    }
  };

  return (
    <div
      className="college-scope"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}
    >
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-ink)', padding: '32px 30px' }}>
          {loading ? (
            <p className="font-mono-editorial" style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
              Checking that link…
            </p>
          ) : error ? (
            <>
              <Eyebrow>That didn't work</Eyebrow>
              <h1 className="font-serif-editorial" style={{ fontSize: 32, fontWeight: 400, margin: '0 0 12px' }}>
                This invite is closed
              </h1>
              <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.6, color: 'var(--college-ink-soft)' }}>
                {error} Ask for a fresh link — they expire after seven days and each one works once.
              </p>
              <DeskButton onClick={() => navigate('/hub')}>Back to your hub</DeskButton>
            </>
          ) : (
            <>
              <Eyebrow>{invite.ownerName} invited you</Eyebrow>
              <h1 className="font-serif-editorial" style={{ fontSize: 34, fontWeight: 400, margin: '0 0 14px', lineHeight: 1.1 }}>
                {invite.ownerName}'s college applications
              </h1>
              <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.6, color: 'var(--college-ink-soft)' }}>
                You'd be joining as {invite.role === 'ADVISOR' ? 'an advisor' : 'a parent'}. This covers their college
                applications only — coursework, grades and their school planner are never part of it, and{' '}
                {invite.ownerName} can change or withdraw any of this at any time.
              </p>

              <div style={{ border: '1px solid var(--college-border-soft)', marginBottom: 22 }}>
                {invite.capabilities.map((capability, i) => (
                  <div
                    key={capability.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '11px 14px',
                      borderBottom: i === invite.capabilities.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                      color: capability.enabled ? 'var(--college-ink)' : 'var(--college-muted-2)',
                    }}
                  >
                    <span style={{ fontSize: 13, width: 12 }}>{capability.enabled ? '✓' : '—'}</span>
                    <span style={{ fontSize: 13 }}>{capability.label}</span>
                  </div>
                ))}
              </div>

              {invite.alreadyAccepted ? (
                <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--college-muted)' }}>
                  You've already accepted this one.
                </p>
              ) : null}

              {invite.emailMatches ? (
                <DeskButton variant="dark" onClick={accept} disabled={accepting}>
                  {accepting ? 'Opening…' : `Open ${invite.ownerName}'s desk`}
                </DeskButton>
              ) : (
                <>
                  <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--college-red)' }}>
                    This invite was sent to <strong>{invite.invitedEmail}</strong>, but you're signed in as{' '}
                    <strong>{user?.email}</strong>. Sign in with the invited address to accept it.
                  </p>
                  <DeskLink onClick={() => navigate('/auth')}>Switch account →</DeskLink>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
